import { useEffect } from 'react';
import { v } from '../constants';
import type { Badge } from '../lib/badges';

// Celebratory modal that pops over the play / publish surface when the
// user just earned one or more badges. Renders the badges one at a
// time — caller's queue advances on dismiss so a single solve that
// unlocks multiple slugs shows each in turn.

interface Props {
  badge: Badge;
  remaining: number;   // count of other badges still queued behind this one
  onDismiss: () => void;
}

export function BadgeAchievementModal({ badge, remaining, onDismiss }: Props) {
  // Escape to dismiss — same affordance as every other modal in the app.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onDismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  return (
    <div className="hm-modal-backdrop hm-achievement-backdrop" role="dialog" aria-modal="true" aria-label="Achievement unlocked">
      <div className="hm-modal hm-achievement-modal" onClick={e => e.stopPropagation()}>
        <div className="hm-achievement-eyebrow">Achievement unlocked</div>
        <div className="hm-achievement-icon" aria-hidden="true">{badge.icon}</div>
        <h2 className="hm-achievement-name">{badge.name}</h2>
        <p className="hm-achievement-desc">{badge.description}</p>
        <div className="hm-achievement-footer">
          {remaining > 0 && (
            <span className="hm-achievement-queue">
              {remaining} more to unlock
            </span>
          )}
          <button type="button" className="hm-primary" onClick={onDismiss} autoFocus>
            {remaining > 0 ? 'Next' : 'Nice'}
          </button>
        </div>
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); onDismiss(); }}
          className="hm-achievement-dismiss"
          style={{ color: v('inkMute') }}
        >Dismiss</a>
      </div>
    </div>
  );
}
